import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { combineLatest, interval, Observable, Subject, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { SwUpdate } from '@angular/service-worker';
import { LetDirective } from '@ngrx/component';
import { TranslateService } from '@ngx-translate/core';

// Components
import { PreloaderComponent } from '@shared/components/preloader/preloader.component';

// Store
import { getIsFetchingWidget, getIsWidgetStatus } from '@store/widget/widget.selectors';
import { getLoader } from '@store/loader/loader.selectors';
import { getPlayAudio } from '@store/play-audio/play-audio.selectors';
import { getSocketToken } from '@store/profile/profile.selectors';

// Services
import { AuthService } from '@services/auth.service';
import { ResizeDetectionService } from '@services/resize-detection.service';
import { CallService } from '@services/call.service';
import { FirebaseService } from '@services/firebase.service';
import { UserService } from '@services/user.service';
import { WebSocketsService } from '@services/web-sockets/web-sockets.service';
import { SystemNotificationsService } from '@services/system-notifications.service';
import { WindowService } from '@services/window.service';
import { DetectBrowserTimezoneService } from '@services/detect-browser-timezone.service';

// Models
import { IChatMessage } from '@interfaces/chat.interface';
import { EWebSocketEvent } from '@services/web-sockets/web-socket.enums';
import { LOCAL_STORAGE_KEYS } from '@constants/storage.constants';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, LetDirective, PreloaderComponent],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
  private serviceWorkerService = inject(SwUpdate);
  private resizeDetectionService = inject(ResizeDetectionService);
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private store = inject(Store);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private userCallService = inject(CallService);
  private firebaseService = inject(FirebaseService);
  private systemNotificationsService = inject(SystemNotificationsService);
  private webSocketsService = inject(WebSocketsService);
  private windowService = inject(WindowService);
  private cdRef = inject(ChangeDetectorRef);
  private readonly detectBrowserTimezoneService = inject(DetectBrowserTimezoneService);
  private translateService = inject(TranslateService);

  @ViewChild('audioElement', { static: false }) audioElement: ElementRef<HTMLAudioElement>;

  private timerId: ReturnType<typeof setTimeout> | null = null;

  public combined$: Observable<{
    isWidget: boolean;
    isFetchingWidget: boolean;
    loaderStatus: boolean;
  }> = this.getCombineObservables();
  private destroy$: Subject<void> = new Subject<void>();
  private socketSubscription$: Subscription;
  private playError = false;

  constructor() {
    if ('GestureEvent' in window) {
      document.addEventListener('gesturestart', e => {
        e.preventDefault();
      });
    }

    this.initServiceWorker();
    this.resizeDetectionService.initService();
  }

  ngOnInit(): void {
    this.detectUpdates();
    this.checkPlayAudioState();
    if (!localStorage.getItem(LOCAL_STORAGE_KEYS.LINK_TYPE)) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.LINK_TYPE, 'WEB');
    }

    this.userCallService.setUserType();
    this.checkUserAuthStatus();
    this.checkSocketTokenChanges();
  }

  ngAfterViewInit(): void {
    if (this.serviceWorkerService.isEnabled) {
      this.serviceWorkerService.checkForUpdate();
    }

    if (
      Object.keys(this.userService.getUser()).length &&
      !window.location.href.includes('deeplink')
    ) {
      this.firebaseService.requestPermission();
    }
  }

  ngAfterContentChecked(): void {
    this.cdRef.detectChanges();
  }

  @HostListener('window:beforeunload')
  ngOnDestroy(): void {
    this.closeWebSocketConnection();
    this.socketSubscription$?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  getCombineObservables(): Observable<{
    isWidget: boolean;
    isFetchingWidget: boolean;
    loaderStatus: boolean;
  }> {
    return combineLatest([
      this.store.select(getIsWidgetStatus).pipe(startWith(null)),
      this.store.select(getIsFetchingWidget).pipe(startWith(null)),
      this.store.select(getLoader).pipe(startWith(null)),
    ]).pipe(
      map(([isWidget, isFetchingWidget, loaderStatus]) => {
        return { isWidget, isFetchingWidget, loaderStatus };
      }),
    );
  }

  playAudio(audioElement: HTMLAudioElement): void {
    audioElement?.load();
    audioElement?.play().catch(() => {
      this.playError = true;
    });
  }

  setAudioInterval(audioElement: HTMLAudioElement): void {
    if (!audioElement) return;
    this.playAudio(audioElement);
    this.timerId = setInterval(() => {
      if (this.playError) {
        clearTimeout(this.timerId);
        return;
      }
      this.playAudio(audioElement);
    }, 3200);
  }

  checkPlayAudioState(): void {
    this.store
      .select(getPlayAudio)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: playAudio => {
          const audioElement: HTMLAudioElement = this.audioElement?.nativeElement;
          if (!playAudio && audioElement) {
            clearInterval(this.timerId);
            return;
          }
          this.setAudioInterval(audioElement);
        },
      });
  }

  initServiceWorker(): void {
    if (!this.serviceWorkerService.isEnabled) return;
    interval(900000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.serviceWorkerService.checkForUpdate();
      });
  }

  detectUpdates(): void {
    this.serviceWorkerService.versionUpdates.pipe(takeUntil(this.destroy$)).subscribe({
      next: event => {
        if (event.type === 'VERSION_DETECTED') {
          console.info(`Downloading new app version: ${event.version.hash}`);
          this.serviceWorkerService.activateUpdate();
        }
        if (event.type === 'NO_NEW_VERSION_DETECTED') {
          console.info(`No new app versions found: ${event.version.hash}`);
        }
        if (event.type === 'VERSION_INSTALLATION_FAILED') {
          console.info(`Failed to install app version '${event.version.hash}': ${event.error}`);
        }
        if (event.type === 'VERSION_READY') {
          console.info(`Current app version: ${event.currentVersion.hash}`);
          console.info(`New app version ready for use: ${event.latestVersion.hash}`);

          if (this.router.url.includes('call')) return;

          this.systemNotificationsService.openUpdateVersionSnackBar();

          const timerId = setTimeout(() => {
            window.location.reload();
            clearTimeout(timerId);
          }, 5000);
        }
      },
    });

    this.serviceWorkerService.unrecoverable.pipe(takeUntil(this.destroy$)).subscribe(event => {
      console.info('App is in unrecoverable state. Reloading to avoid chunk load issue.', event);
      this.systemNotificationsService.openErrorSnackBar(
        this.translateService.instant('common.unrecoverable-error', { reason: event.reason }),
      );
    });
  }

  checkSocketTokenChanges(): void {
    this.store
      .select(getSocketToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: socketToken => {
          if (socketToken === null) {
            this.closeWebSocketConnection();
            return;
          }
          const instanceToken = this.webSocketsService.getSocketToken();
          if (instanceToken === socketToken) return;
          this.closeWebSocketConnection();
          this.openWebSocketConnection();
        },
      });
  }

  checkUserAuthStatus(): void {
    this.authService.isAuthUser$.pipe(takeUntil(this.destroy$), distinctUntilChanged()).subscribe({
      next: authStatus => {
        if (!authStatus) {
          this.closeWebSocketConnection();
          return;
        }
        this.openWebSocketConnection();
      },
    });
  }

  openWebSocketConnection(): void {
    this.webSocketsService.createConnection();
    if (this.socketSubscription$) {
      this.socketSubscription$.unsubscribe();
    }
    this.socketSubscription$ = this.webSocketsService.messagesSubject$.subscribe({
      next: data => {
        if (!data) return;
        switch (data.type) {
          case EWebSocketEvent.ChatMessage:
            this.sendMessageNotification(data.data);
            break;
        }
      },
    });
  }

  closeWebSocketConnection(): void {
    this.webSocketsService.disconnectSocket();
  }

  sendMessageNotification(data: IChatMessage): void {
    const callId = +this.activatedRoute.snapshot.queryParams?.call_id;
    if (this.router.url.includes('call-process') || callId === data.call_id) {
      return;
    }
    this.windowService.changeTitle();
    this.systemNotificationsService.openMessageNotificationSnackBar(
      this.translateService.instant('common.new-engineer-message', {
        firstName: data.sender_first_name,
        lastName: data.sender_last_name,
      }),
      data.call_id,
    );
  }
}
